import React from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  GroupsOutlined as GroupsIcon,
  Chat as ChatIcon,
  Poll as PollIcon,
  AttachMoney as MoneyIcon,
  Notifications as NotifyIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { getFAQSchema } from '../utils/seoSchemas';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import { DOCUMENT, documentPageCanvasSx, PAGE_CONTAINER_MAX_WIDTH} from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from '../components/PartnerListChrome';
import { useTranslation } from 'react-i18next';

const GroupVacation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const features = [
    {
      icon: <GroupsIcon sx={{ fontSize: 36, color: '#8B1538' }} />,
      title: 'Invite Your Group',
      description: 'Add friends, family, or colleagues with a simple invite link. Everyone stays in sync with the trip plans.',
    },
    {
      icon: <ChatIcon sx={{ fontSize: 36, color: '#8B1538' }} />,
      title: 'Group Chat',
      description: 'Built-in chat keeps all trip discussions in one place. No more scattered WhatsApp groups.',
    },
    {
      icon: <PollIcon sx={{ fontSize: 36, color: '#8B1538' }} />,
      title: 'Vote on Activities',
      description: 'Can\'t decide where to eat? Let the group vote! Democratic decision-making made easy.',
    },
    {
      icon: <MoneyIcon sx={{ fontSize: 36, color: '#8B1538' }} />,
      title: 'Split Expenses',
      description: 'Track who paid for what and split costs fairly. No awkward money conversations.',
    },
    {
      icon: <NotifyIcon sx={{ fontSize: 36, color: '#8B1538' }} />,
      title: 'Real-time Updates',
      description: 'Everyone sees changes instantly. No confusion about the latest plans.',
    },
  ];

  const useCases = [
    {
      title: 'Family Vacations',
      description: 'Coordinate with siblings, parents, and kids. Let everyone contribute ideas for the perfect family trip.',
      color: '#e3f2fd',
    },
    {
      title: 'Friends Getaways',
      description: 'Bachelor parties, girls trips, or reunion weekends. Keep everyone organized and excited.',
      color: '#f3e5f5',
    },
    {
      title: 'Destination Weddings',
      description: 'Coordinate travel for wedding guests. Share itineraries, hotel info, and local recommendations.',
      color: '#fff3e0',
    },
    {
      title: 'Corporate Retreats',
      description: 'Plan team offsites and company trips. Manage logistics for groups of any size.',
      color: '#e8f5e9',
    },
  ];

  const faqs = [
    {
      question: 'How many people can I add to a group trip?',
      answer: 'There\'s no limit! You can invite as many people as you want to your trip. Whether it\'s a couple or a group of 50, our platform handles it all.',
    },
    {
      question: 'Can group members edit the itinerary?',
      answer: 'Yes, you control permissions. You can allow everyone to edit, or keep editing rights for yourself while others can only view and suggest.',
    },
    {
      question: 'How does expense splitting work?',
      answer: 'Add expenses as they happen, assign who paid and who owes. At the end of the trip, the app calculates exactly who owes whom and how much.',
    },
    {
      question: 'Is the group chat feature free?',
      answer: 'Yes! Group chat, voting, and all collaboration features are completely free. No premium plans required.',
    },
    {
      question: 'Can I plan a trip without inviting others first?',
      answer: 'Absolutely! Start planning solo and invite your group whenever you\'re ready. You can add members at any stage of the planning process.',
    },
  ];

  const faqSchema = getFAQSchema(faqs);

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Group Vacation Planner",
      "description": "Plan group vacations and coordinate travel with friends and family",
      "url": "https://dejavunomad.com/group-vacation",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Déjà Vu Nomad",
        "url": "https://dejavunomad.com"
      }
    },
    faqSchema
  ];

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title="Group Vacation Planner | Plan Group Trips with Friends & Family - Déjà Vu Nomad"
        description="Plan group vacations the easy way. Collaborate with friends and family, vote on activities, split expenses, and keep everyone organized. The best free group trip planner for 2026."
        keywords="group vacation planner, group trip planning, group travel, family vacation planner, friends trip planner, group travel planning, travel with friends, group travel app, collaborative trip planning, group holiday planning"
        canonical="https://dejavunomad.com/group-vacation"
        structuredData={structuredData}
      />

      <DocumentMarketingHero
        eyebrow={t('home.footer.groupTrips')}
        title="Plan Group Vacations Without the Chaos"
        subtitle="Tired of endless group chats and spreadsheets? Our group vacation planner keeps everyone on the same page. Collaborate in real-time, vote on activities, and split expenses effortlessly."
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              ...partnerOxbloodBtnSx,
              bgcolor: '#C9A961',
              color: DOCUMENT.nav,
              '&:hover': { bgcolor: '#DCC183', boxShadow: 'none' },
            }}
          >
            Start Group Trip Free
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/deals')}
            sx={{
              ...partnerOutlineBtnSx,
              color: DOCUMENT.ivory,
              borderColor: 'rgba(246,239,228,.45)',
              '&:hover': {
                background: 'rgba(246,239,228,0.08)',
                borderColor: '#C9A961',
                boxShadow: 'none',
              },
            }}
          >
            View Group Deals
          </Button>
        </Stack>
      </DocumentMarketingHero>

      {/* Features Section */}
      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 1 }}>
              Built for Group Travel
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
              Every feature designed to make group vacation planning stress-free
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                  <CardContent>
                    <Box sx={{ mb: 1.5 }}>{feature.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: '1rem' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Use Cases Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 1 }}>
              Perfect for Any Group Trip
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {useCases.map((useCase, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={{ height: '100%', backgroundColor: useCase.color }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: '1rem' }}>
                      {useCase.title}
                    </Typography>
                    <Typography variant="body2">
                      {useCase.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits List */}
      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 1 }}>
              Why Groups Love Our Planner
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {[
              'No more "Where should we stay?" debates - everyone votes',
              'See who\'s confirmed and who\'s still deciding',
              'Automatic expense calculations - no more IOUs',
              'Everyone has access to the full itinerary',
              'Changes sync instantly across all devices',
              'Works for groups of 2 or 200',
              'Free for all group members - no per-person fees',
              'Mobile app for planning on the go',
            ].map((benefit, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckIcon sx={{ color: '#2e7d6c', fontSize: '1.2rem' }} />
                  <Typography variant="body2">{benefit}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 1 }}>
              Group Trip Planning FAQ
            </Typography>
          </Box>
          {faqs.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 4, md: 5 }, backgroundColor: '#8B1538', color: 'white' }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: '1.4rem', md: '1.75rem' } }}>
              Ready to Plan Your Group Vacation?
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
              Create your trip in seconds and invite your group
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={partnerOxbloodBtnSx}
            >
              Plan Your Group Trip Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default GroupVacation;
